import { useTranslate } from '@tolgee/react';
import { Tab } from './state';
import { memo, useCallback, useMemo } from 'react';
import { css, cx } from '@emotion/css';
import { Tooltip } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useGetTitleI18nList } from './share';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';

export interface PageTabProps {
  tab: Tab;
  active: boolean;
  onActive: (path: string) => void;
  onClose: (path: string) => void;
}

const PageTab = memo((props: PageTabProps) => {
  const { tab, active, onActive, onClose } = props;

  const getTitleI18nList = useGetTitleI18nList();
  const { path } = tab;

  const { qaEnable, whiteListConfig: { LLM, QA_AI_3, QA_NEW_KNOWLEDGE } = {} } =
    useOrgConfigStateValue();

  const hardcodeTitleI18nList = useMemo(() => {
    if (
      tab.path === '/aide/review/review/PRAISE_REMINDER' &&
      ['TIKTOK', 'TIKTOK_US'].some((e) => tab.search.indexOf(e) > -1)
    ) {
      return ['邀请评价'];
    }
    if (
      [
        '/aide/scenario/policy/PRAISE_REMINDER/TIKTOK',
        '/aide/scenario/policy/PRAISE_REMINDER/TIKTOK_US',
      ].some((e) => tab.path.startsWith(e))
    ) {
      if (tab.path.includes('create')) {
        return ['marketing.新增', '邀请评价'];
      } else if (tab.path.includes('update')) {
        return ['marketing.编辑', '邀请评价'];
      } else if (tab.path.includes('copy')) {
        return ['marketing.复制', '邀请评价'];
      }
    }
    return;
  }, [tab]);

  const titleI18nList = useMemo(
    () => getTitleI18nList(path, qaEnable ? QA_AI_3 || QA_NEW_KNOWLEDGE : LLM),
    [getTitleI18nList, path, qaEnable, QA_AI_3, QA_NEW_KNOWLEDGE, LLM],
  );

  const title = useTitleRender().titleRender(hardcodeTitleI18nList || titleI18nList);

  return (
    <li className={cx(styles.tab, active && styles.tabActive)} onClick={() => onActive(path)}>
      <span />
      <Tooltip overlayClassName={styles.tooltip} title={title}>
        <div>{title}</div>
      </Tooltip>
      <CloseOutlined
        style={active ? { display: 'block' } : undefined}
        onClick={(e) => {
          e.stopPropagation();
          onClose(path);
        }}
      />
      <sub />
      <sup />
    </li>
  );
});

export const useTitleRender = () => {
  const { t } = useTranslate();

  const titleRender = useCallback(
    (titleI18nList: (string | string[])[]) => {
      return titleI18nList
        .map((stringOrList) => {
          if (typeof stringOrList === 'string') {
            return t(stringOrList);
          }

          const [key, ns] = stringOrList;

          return t(key, { ns });
        })
        .join(' - ');
    },
    [t],
  );

  return {
    t,
    titleRender,
  };
};

const styles = {
  tab: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: relative;
    height: 32px;
    margin: 0 8px;
    padding: 0 8px;
    cursor: pointer;
    > span:first-of-type {
      position: absolute;
      top: 8px;
      left: -6px;
      height: 16px;
      border-left: solid 2px #f0f0f0;
      display: none;
    }
    > div {
      flex: 1 0 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    > .anticon-close {
      display: none;
    }
    & + & {
      > span:first-of-type {
        display: block;
      }
    }
    &:hover {
      > div {
        color: var(--ant-primary-color);
      }
      > .anticon-close {
        display: block;
      }
    }
  `,
  tabActive: css`
    background: #f0f2f5;
    border-radius: 8px 8px 0 0;
    > div {
      color: var(--ant-primary-color);
    }
    &::before,
    &::after {
      content: '';
      position: absolute;
      top: 0;
      width: 8px;
      height: 100%;
      background: #f0f2f5;
    }
    &::before {
      left: -4px;
      border-top-left-radius: 8px;
      transform: skewX(-8deg);
    }
    &::after {
      right: -4px;
      border-top-right-radius: 8px;
      transform: skewX(8deg);
    }
    > sub,
    > sup {
      position: absolute;
      top: 0;
      width: 8px;
      height: 100%;
      background: #f0f2f5;
      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: #fff;
      }
    }
    > sub {
      left: -12px;
      transform: skewX(-8deg);
      &::before {
        border-bottom-right-radius: 8px;
      }
    }
    > sup {
      right: -12px;
      transform: skewX(8deg);
      &::before {
        border-bottom-left-radius: 8px;
      }
    }
  `,
  tooltip: css`
    .ant-tooltip-arrow {
      display: none;
    }
    .ant-tooltip-inner {
      background-color: rgba(0, 0, 0, 0.7);
    }
  `,
};

export default PageTab;
